// Load component modules only when their markup is present. Hidden interaction
// components stay dormant until needed; dynamically rendered sections use the same path.
import { loadStylesheet } from '@theme/stylesheet-loader';

const manifest = document.querySelector('script[data-component-modules]');
const rules = manifest ? JSON.parse(manifest.textContent) : [];
const requests = new Map();
const ready = new Set();
const observed = new WeakSet();
const pendingClicks = new WeakSet();
const selector = rules.map((rule) => rule.selector).join(',');

function load(rule) {
  if (requests.has(rule.src)) return requests.get(rule.src);
  const promise = Promise.all([
    import(rule.src),
    ...(rule.css ? [loadStylesheet(rule.css)] : []),
  ]).then(() => {
    ready.add(rule.src);
  }).catch((error) => {
    requests.delete(rule.src);
    throw error;
  });
  requests.set(rule.src, promise);
  return promise;
}

function preload(rule) {
  load(rule).catch((error) => console.warn('[theme components]', error));
}

const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    observer.unobserve(entry.target);
    for (const rule of rules) {
      if (rule.mode === 'visible' && entry.target.matches(rule.selector)) preload(rule);
    }
  }
}, { rootMargin: '300px' }) : null;

function discover(root) {
  if (!selector || !(root instanceof Element || root instanceof Document)) return;
  const nodes = [...root.querySelectorAll(selector)];
  if (root instanceof Element && root.matches(selector)) nodes.unshift(root);
  for (const node of nodes) {
    for (const rule of rules) {
      if (!node.matches(rule.selector) || rule.mode === 'interaction') continue;
      if (rule.mode !== 'visible' || !observer || window.Shopify?.designMode) preload(rule);
      else if (!observed.has(node)) {
        observed.add(node);
        observer.observe(node);
      }
    }
  }
}

// Declarative actions can address a component outside the clicked element,
// e.g. a gallery slide opening its sibling zoom dialog.
function interactionRules(target) {
  if (!(target instanceof Element)) return [];
  const action = target.closest('[on\\:click]')?.getAttribute('on:click');
  const id = action?.startsWith('#') ? action.slice(1).split('/')[0] : null;
  const addressed = id ? document.getElementById(id) : null;
  return rules.filter((rule) => {
    const node = target.closest(rule.selector) || (addressed?.matches(rule.selector) ? addressed : null);
    return node && (!customElements.get(node.localName) || (rule.css && !ready.has(rule.src)));
  });
}

// Intent starts the download early. Capture the first click if an upgrade is
// still pending, then replay once so the theme's existing action handler runs.
for (const eventName of ['pointerover', 'focusin']) {
  document.addEventListener(eventName, (event) => interactionRules(event.target).forEach(preload), { passive: true });
}
window.addEventListener('click', async (event) => {
  const needed = interactionRules(event.target);
  if (!needed.length || !(event.target instanceof Element)) return;
  const target = event.target;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (pendingClicks.has(target)) return;
  pendingClicks.add(target);
  try {
    await Promise.all(needed.map(load));
    if (target.isConnected) target.dispatchEvent(new MouseEvent('click', {
      bubbles: true, cancelable: true, composed: true, view: window,
      ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey, altKey: event.altKey,
    }));
  } catch (error) {
    // Leave the original control in place for another attempt.
    console.warn('[theme components]', error);
  } finally {
    pendingClicks.delete(target);
  }
}, true);

if (selector) {
  discover(document);
  new MutationObserver((records) => {
    // Inspect only added subtrees, never rescan the entire page on every mutation.
    for (const record of records) for (const node of record.addedNodes) discover(node);
  }).observe(document.documentElement, { childList: true, subtree: true });
}
